<template>
  <div>
    <!-- overlay -->
    <div
      v-if="visible"
      class="walkthrough-overlay"
    >
      <div class="walkthrough-modal">
        <button class="walkthrough-close" @click="close">&times;</button>

        <!-- Header: numbered bullets + description -->
        <div class="hint-header">
          <div class="swiper-pagination"></div>
          <p class="hint-description">{{ steps[currentIndex]?.description }}</p>
        </div>

        <!-- Swiper showing only images -->
        <swiper
          ref="swiperRef"
          :modules="modules"
          :loop="true"
          :pagination="{
            el: '.swiper-pagination',
            clickable: true,
            renderBullet: (i, className) => `<span class='${className}'>${i+1}</span>`
          }"
          class="hint-swiper"
          @swiper="onSwiperInitialized"
          @slideChange="onSlideChange"
        >
          <swiper-slide
            v-for="(step, idx) in steps"
            :key="idx"
            class="swiper-slide"
          >
            <div class="slide-image-wrapper">
              <img :src="step.img" :alt="step.title" />
            </div>
          </swiper-slide>
        </swiper>

      </div>
    </div>
  </div>
</template>

<script>
import { ref, onMounted, nextTick, watch } from 'vue';
import { Swiper, SwiperSlide } from 'swiper/vue';
import { Pagination } from 'swiper/modules';
import 'swiper/css';
import 'swiper/css/pagination';

export default {
  name: 'Walkthrough',
  components: {
    swiper: Swiper,
    'swiper-slide': SwiperSlide
  },
  props: {
    steps: {
      type: Array,
      required: true,
      default: () => [
        { img: '/assets/images/tutorial/1.png', title: '', description: 'Select a product to customize' },
        { img: '/assets/images/tutorial/2.png', title: '', description: 'Customize product with preferred method' },
        { img: '/assets/images/tutorial/3.png', title: '', description: 'Repeat process for all products' }
      ]
    },
    autoShow: { type: Boolean, default: true }
  },
  emits: ['close'],
  setup(props, { emit }) {
    const visible = ref(false);
    const currentIndex = ref(0);
    const swiperRef = ref(null);
    const swiperInstance = ref(null);

    const modules = [Pagination];

    const open = () => { visible.value = true; };
    const close = () => { visible.value = false; emit('close'); };
    
    const goNext = () => {
      if (swiperInstance.value) {
        swiperInstance.value.slideNext();
      }
    };

    const onSwiperInitialized = (swiper) => {
      console.log('Swiper initialized');
      swiperInstance.value = swiper;
    };

    const onSlideChange = (swiper) => {
      console.log('Slide changed', swiper.realIndex);
      currentIndex.value = swiper.realIndex;
    };

    // Get current description text
    const currentDescription = () => {
      return props.steps[currentIndex.value]?.description || '';
    };

    onMounted(() => {
      if (props.autoShow) open();
    });

    // Watch for steps changes
    watch(() => props.steps, (newSteps) => {
      console.log('Steps updated', newSteps);
    });

    // For debugging
    watch(currentIndex, (newIndex) => {
      console.log('currentIndex changed to:', newIndex);
    });

    return { 
      visible, 
      currentIndex, 
      modules, 
      goNext, 
      close, 
      swiperRef,
      currentDescription,
      onSwiperInitialized,
      onSlideChange
    };
  }
};
</script>

<style scoped>
.swiper-pagination{
  position: relative;
}
.walkthrough-overlay {
  position: fixed; top: 0; right: 0; bottom: 0; left: 0;
  background: rgba(0, 0, 0, 0.7);
  display: flex; align-items: center; justify-content: center;
  z-index: 1000;
}

.walkthrough-modal {
  position: relative;
  width: 90vw; max-width: 500px;
  background: #fff; border-radius: 12px;
  padding: 50px 20px 20px 20px; /* 增加顶部padding，为更大的关闭按钮留出空间 */
  box-shadow: 0 4px 12px rgba(0,0,0,0.3);
}

.walkthrough-close {
  position: absolute; 
  top: 10px; 
  right: 10px;
  background: rgba(0, 0, 0, 0.05); 
  border: none;
  font-size: 48px; 
  cursor: pointer;
  /* 确保按钮更大且内容居中 */
  padding: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 70px;
  height: 70px;
  z-index: 10;
  color: #666;
  transition: all 0.3s ease;
  line-height: 1;
  border-radius: 50%;
}

.walkthrough-close:hover {
  color: #000;
  background-color: rgba(0, 0, 0, 0.1);
  transform: scale(1.15);
}

.hint-header {
  text-align: center; 
  margin-bottom: 12px;
  padding-top: 20px; /* 为更大的关闭按钮留出空间 */
}

.swiper-pagination {
  display: flex; 
  justify-content: center; 
  gap: 15px; /* 增加按钮间距 */
  margin-bottom: 12px;
}

:deep(.swiper-pagination-bullet) {
  width: 80px;
  height: 80px;
  border: 6px solid #ddd; 
  border-radius: 50%;
  color: #ddd; 
  font-size: 40px; 
  font-weight: bold;
  background: transparent;
  padding: 0;
  margin: 0;
  box-sizing: border-box;
  cursor: pointer;
  transition: all 0.3s ease;
  /* 使用flex布局确保数字完全居中 */
  display: flex;
  align-items: center;
  justify-content: center;
  opacity: 1;
  line-height: 1;
  position: relative;
}

:deep(.swiper-pagination-bullet:hover) {
  transform: scale(1.1);
  border-color: #999;
  color: #999;
}

:deep(.swiper-pagination-bullet-active) {
  border-color: green; 
  color: green; 
}

:deep(.swiper-pagination-bullet-active:hover) {
  transform: scale(1.1);
  border-color: green;
  color: green;
}

.hint-description {
  font-size: 32px; color: #666;
  margin: 0;
}

.hint-swiper {
  width: 100%;
}

.slide-image-wrapper {
  display: flex; justify-content: center;
}

.slide-image-wrapper img {
  max-width: 100%; border-radius: 8px;
}

.next-arrow {
  position: absolute; bottom: 16px; left: 50%;
  transform: translateX(-50%);
  background: #eee; border: none; border-radius: 50%;
  width: 40px; height: 40px; font-size: 1.5rem;
  cursor: pointer; display: flex; align-items: center; justify-content: center;
}

.next-arrow:hover { background: #ddd; }
</style>